import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Landmark, Sparkles, TrendingUp, ChevronRight } from "lucide-react";
import api from "../../lib/api";
import { Pill } from "../common/Primitives";

/**
 * DebtSuitabilityTile — reads /api/debt/suitability (heuristic driven by
 * the caller's financial_profile + company_intelligence) and shows a
 * personalised "should we raise debt?" panel with reasoning + product
 * suggestions. Founder-only; silently hides if the endpoint errors.
 */
export default function DebtSuitabilityTile() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get("/debt/suitability")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) return null;
  if (!data || data.suitability === "unknown") return null;

  const tone = {
    strong: { bg: "bg-emerald-500/8", border: "border-emerald-500/30", text: "text-emerald-800", pill: "success" },
    moderate: { bg: "bg-navy/[0.03]", border: "border-navy/20", text: "text-navy", pill: "gold" },
    explore: { bg: "bg-gold/10", border: "border-gold/30", text: "text-[#8A6600]", pill: "gold" },
    not_yet: { bg: "bg-rose-500/5", border: "border-rose-500/25", text: "text-rose-800", pill: "muted" },
  }[data.suitability] || { bg: "bg-navy/[0.03]", border: "border-navy/15", text: "text-ink", pill: "muted" };

  const badge = {
    strong: "Strong debt fit",
    moderate: "Solid debt fit",
    explore: "Worth exploring",
    not_yet: "Build profile first",
  }[data.suitability] || "Debt suitability";

  return (
    <div
      className={`border ${tone.border} ${tone.bg} rounded-md p-4 mb-6`}
      data-testid="debt-suitability-tile"
    >
      <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Landmark size={16} className={tone.text} />
          <div className="overline">VenturaZ Money · Suitability</div>
          <Pill tone={tone.pill}>{badge}</Pill>
          {data.score != null && (
            <span className="text-[10px] font-mono text-ink-muted">Score {data.score}/100</span>
          )}
        </div>
        <Link
          to={data.eligible ? "/money?prefill=1" : "/startup/financial-profile"}
          className="text-xs font-heading bg-navy text-white hover:bg-navy/90 rounded px-3 py-1.5 inline-flex items-center gap-1"
          data-testid="debt-suitability-cta"
        >
          {data.eligible ? "Raise Debt · One click" : "Complete Financial Profile"} <ChevronRight size={12} />
        </Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="md:col-span-2 space-y-1">
          <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted flex items-center gap-1">
            <Sparkles size={10} /> Reasoning from your profile
          </div>
          <ul className="text-xs text-ink-soft space-y-0.5 list-disc pl-4">
            {(data.reasons || []).map((r, i) => (
              <li key={i} data-testid={`debt-suit-reason-${i}`}>{r}</li>
            ))}
          </ul>
        </div>
        <div className="space-y-2">
          {data.suggested_amount_inr != null && (
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">Indicative ticket</div>
              <div className={`font-display text-lg ${tone.text}`} data-testid="debt-suit-amount">
                ₹{Number(data.suggested_amount_inr).toLocaleString("en-IN")}
              </div>
            </div>
          )}
          {(data.suggested_products || []).length > 0 && (
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted flex items-center gap-1">
                <TrendingUp size={10} /> Suggested products
              </div>
              <div className="flex flex-wrap gap-1 mt-1">
                {data.suggested_products.map((p) => (
                  <span key={p} className="text-[10px] font-mono px-2 py-0.5 rounded bg-navy/5 text-ink-soft"
                    data-testid={`debt-suit-product-${p.replace(/\s|\//g, "_")}`}>{p}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="mt-2 text-[10px] font-mono text-ink-muted">
        Heuristic based on your latest financial profile — not a lender commitment. Our team reviews every case manually.
      </div>
    </div>
  );
}
